<template>
  <div>
    <div class="voucher-section">
      <div class="d-flex justify-between items-center">
        <div class="voucher-heading body-s-medium">
          กรอกโค้ด TRD จากรายการ Trade-in ที่คุณทำรายการไว้
        </div>

        <div class="voucher-counter">
          {{ value.length }}/{{ max }}
        </div>
      </div>

      <div class="scan-voucher-btn">
        <v-btn
          outlined
          rounded
          height="42"
          color="primary"
          :disabled="value.length >= max"
          @click="onScan"
        >
          <v-icon size="16" class="mr-1">
            mdi-qrcode-scan
          </v-icon>
          สแกนโค้ด TRD
        </v-btn>
      </div>

      <div class="divider-or"><span>หรือ</span></div>

      <BaseTradeInVoucherItem
        v-for="(voucher, index) in value"
        :ref="`voucher-item-${index}`"
        :key="index"
        :value="voucher.code"
        :type="type"
        :valid="voucher.valid"
        :amount="voucher.amount"
        :branch="voucher.branch"
        :branchName="voucher.branch_name"
        :branchError="voucher.reason === 'branch_mismatch'"
        :errorMessage="voucher.message"
        @input="onInput(index, $event)"
        @confirm="onConfirm"
        @confirmRemove="onConfirmRemove(index)"
        @remove="onRemove(index)"
      />

      <template v-if="value.length === 0">
        <div class="voucher-empty">
          สแกน QR Code หรือกรอกโค้ด TRD เพื่อยืนยันรายการ Trade-in
        </div>
        <div v-if="hasRequiredError" class="voucher-error">
          <v-icon size="16" color="#FF4252">
            mdi-alert-circle-outline
          </v-icon>
          <span>กรุณาเพิ่มรายการ Trade-in อย่างน้อย 1 รายการ</span>
        </div>
      </template>

      <div class="voucher-footer">
        <div class="voucher-hint" @click="showHelpModal = true">
          <span>โค้ด TRD อยู่ที่ไหน</span>
          <v-icon size="16" color="rgba(0,0,0,0.45)">
            mdi-help-circle-outline
          </v-icon>
        </div>

        <div class="voucher-actions">
          <v-btn
            small
            text
            rounded
            height="42"
            color="primary"
            :disabled="value.length >= max"
            @click="onAdd"
          >
            <v-icon size="16" class="mr-1">
              mdi-plus
            </v-icon>
            เพิ่มโค้ด TRD เอง
          </v-btn>

          <v-btn
            depressed
            rounded
            height="42"
            :dark="value.length > 0"
            :disabled="value.length === 0"
            color="primary"
            :loading="isLoading"
            @click="onConfirm"
          >
            ตรวจสอบ
          </v-btn>
        </div>
      </div>
    </div>

    <QrCodeReader ref="qrCodeReaderRef" @result="onScanResult" />

    <v-dialog v-model="dialogRemove" max-width="320">
      <v-card>
        <v-card-text class="py-4">
          <v-row>
            <v-col cols="12" class="text-center">
              <div class="header-modal-title">
                ยืนยันลบรายการ Trade-in นี้?
              </div>
            </v-col>
            <v-col cols="12" class="text-center">
              หลังจากลบแล้วจะไม่สามารถกู้คืนได้
              <br />
              ต้องทำการเพิ่มและกรอกโค้ดใหม่อีกครั้ง
            </v-col>

            <v-col cols="6">
              <v-btn
                dark
                block
                depressed
                class="font-bold"
                color="#FF4252"
                @click="onRemove"
              >
                ลบรายการ
              </v-btn>
            </v-col>

            <v-col cols="6">
              <v-btn
                block
                depressed
                link
                @click="dialogRemove = false"
              >
                ยกเลิก
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <BaseTradeInVoucherHelpModal v-model="showHelpModal" />
  </div>
</template>

<script>
import BaseTradeInVoucherItem from './BaseTradeInVoucherItem.vue'
import BaseTradeInVoucherHelpModal from './BaseTradeInVoucherHelpModal.vue'

export default {
  name: 'BaseTradeInVoucherSection',

  components: {
    BaseTradeInVoucherItem,
    BaseTradeInVoucherHelpModal,
    QrCodeReader: () => import('@/components/bnn/QrCodeReader.vue'),
  },

  props: {
    value: {
      type: Array,
      default: () => [{ code: '' }],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: '',
    },
    max: {
      type: Number,
      default: 3,
    },
    hasRequiredError: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      dialogRemove: false,
      removeIndex: null,
      showHelpModal: false,
    }
  },

  methods: {
    emitVouchers(vouchers) {
      this.$emit('input', vouchers)
      this.$emit('change', vouchers)
    },

    onInput(index, code) {
      const vouchers = this.value.map((voucher, i) =>
        i === index ? { ...voucher, code } : voucher,
      )
      this.emitVouchers(vouchers)
    },

    onScan() {
      // Add a new voucher if the last one is filled
      if (
        this.value.length === 0 ||
        this.value[this.value.length - 1].code !== ''
      ) {
        this.onAdd()
      }

      // Index of the last voucher to scan
      const index = this.value.length - 1

      this.$nextTick(() => {
        this.$refs.qrCodeReaderRef.openScanner(index)
      })
    },

    onScanResult(decodedText, index) {
      this.onInput(index, decodedText)

      this.onConfirm()
    },

    onAdd() {
      if (this.value.length >= this.max) return
      this.emitVouchers([...this.value, { code: '' }])
    },

    onConfirmRemove(index) {
      this.removeIndex = index
      this.dialogRemove = true
    },

    onRemove(index) {
      if (this.removeIndex === null) {
        this.removeIndex = index
      }

      this.emitVouchers(
        this.value.filter((_, i) => i !== this.removeIndex),
      )
      this.dialogRemove = false
      this.removeIndex = null

      this.$emit('remove')
    },

    onConfirm() {
      this.$emit('verify', this.value)
    },
  },
}
</script>

<style lang="stylus" scoped>
.voucher-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
	padding: 16px;
	background-color: #FFFFFF;
    border: 1px solid #D9D9D9;
	border-radius: 8px;
}

.voucher-footer,
.voucher-actions {
    display: flex;
    align-items: center;
    gap: 16px;
}

.voucher-footer {
	flex-direction: column;
	justify-content: flex-end;
	align-items: flex-start;

	@media screen and (min-width: 768px) {
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
	}

}

.voucher-actions {
	flex-direction: row;

	@media screen and (max-width: 767px) {
		width: 100%;

		> * {
			flex: auto;
		}
	}
}

.voucher-hint {
    display: flex;
    align-items: center;
    gap: 4px;

    font-size: 14px;
    color: rgba(#000000, 0.45);

	cursor: pointer;
}

.voucher-counter {
    font-size: 14px;
    color: rgba(#000000, 0.45);
    margin-left: auto;
}

.header-modal-title {
    font-size: 20px;
    font-weight: bold;
    align-self: center;
}

.voucher-empty {
	padding: 16px;
	font-size: 14px;
	text-align: center;
	color: rgba(#000000, 0.45);
	border: 1px dashed #d9d9d9;
	border-radius: 8px;
}

.voucher-error {
    display: flex;
    align-items: center;
    gap: 6px;

    font-size: 14px;
    color: #FF4252;
}

.scan-voucher-btn {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;

	font-size: 14px;
	color: rgba(#000000, 0.45);
}

.divider-or {
    display: flex;
    align-items: center;
    gap: 12px;

    color: rgba(#000000, 0.35);
    font-size: 12px;

    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: #E0E0E0;
    }
}
</style>
